import CompatibilityImportModal from './Compatibility/CompatibilityImportModal';
import {
  KoobTooltip,
  KoobInputSegmented,
  KoobInputSelect,
  KoobSegmentedControl,
  KoobSegmentedControlItem,
  KoobPagination,
  KoobListPageLayout,
  KoobCreateButton,
  KoobButtonMenu,
  KoobMenuItem,
} from '@koob/ui';
import { useApp, useError, useLanguage, useSearchParams } from 'hooks';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import {
  exportExperiencesCompatibilities,
  getExperiences,
} from '../../api/node/experiences';
import { getSegments } from '../../api/node/segments';
import { getThemes } from '../../api/node/themes';
import { Header } from '../../components';
import LocationAwareSearch from '../../components/LocationAwareSearch';
import { Container } from '../../ui';
import { getLanguages } from '../../utils/language';
import ExperiencesListComponent from './List/partials/ExperiencesList';
import ExperiencesMap from './List/partials/ExperiencesMap';
import FilterExperiences from './List/partials/FilterExperiences';
import { viewKind } from 'utils';
import { getOrganizations } from 'api/node/organization';
import { ExperiencePricingTypeEnum, RoleEnum } from '@koob/enums';
import { PER_PAGE_FOR_CATALOG } from 'constants';

export default function ExperiencesList() {
  const { t } = useTranslation('experiences');
  const [searchParams, setSearchParams] = useSearchParams();
  const [kind, setKind] = useState('list');
  const [type, setType] = useState(undefined);
  const [selectedOrganization, setSelectedOrganization] = useState(undefined);
  const { state } = searchParams;
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState();
  const [perPage, setPerPage] = useState(PER_PAGE_FOR_CATALOG);
  const [extractProcessing, setExtractProcessing] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [filters, setFilters] = useState({});

  const { user } = useApp();

  const pricingType = [
    ExperiencePricingTypeEnum.SIC,
    ExperiencePricingTypeEnum.SERIES,
    ExperiencePricingTypeEnum.FIT,
  ].includes(type?.value?.split('.')?.[1])
    ? type?.value?.split('.')?.[1]
    : null;

  const experiencesFilters = {
    page,
    state,
    types: type?.value ? [type?.value?.split('.')[0]] : [],
    organizationId: selectedOrganization?.value,
    search: searchParams.search ?? '',
    perPage: perPage,
    themes: (filters?.themes || []).map(({ id }) => id),
    segments: (filters?.segments || []).map(({ id }) => id),
    startingPoint: filters?.startingPoint,
    endingPoint: filters?.endingPoint,
    minDuration: filters?.expDuration?.min,
    maxDuration: filters?.expDuration?.max,
    sustainableLevels: (filters?.sustainableLevels || [])
      .map(({ value }) => value)
      .filter((item) => Boolean(item)),
    languages: (filters?.languages || []).map(({ value }) => value),
    pricingType,
    transitionProgramsOnly: type?.value === 'Program.transition',
    includePartnerExperiences: filters?.includePartnerExperiences ?? true,
  };

  const { sendErrorSnack } = useError();
  const { lang } = useLanguage();
  const { data, isLoading, reload } = useAsync({
    promiseFn: getExperiences,
    filters: experiencesFilters,
    locale: lang,
    watchFn: (props, prevProps) => {
      return (
        !isEqual(props.filters, prevProps.filters) ||
        props.locale !== prevProps.locale
      );
    },
    onResolve: (response) => {
      setTotalPages(response?.headers?.xTotalPages);
    },
  });
  const totalCount = Number(data?.headers?.xTotalCount || 0);

  const experiences = data?.data ?? [];
  const stateOptions = [
    { value: null, label: t('state.all') },
    { value: 'available', label: t('state.available') },
    { value: 'in_progress', label: t('state.inProgress') },
    { value: 'stand_by', label: t('state.standBy') },
  ];
  const isEmpty = experiences.length === 0;

  useEffect(() => {
    setPage(1);
  }, [searchParams.search, type]);

  const { data: organizationDatas, isLoading: organizationsLoading } = useAsync(
    {
      promiseFn: getOrganizations,
      filters: {
        isPartner: false,
        discarded: false,
      },
    },
  );

  const organizationOptions = (organizationDatas?.data ?? [])?.map(
    ({ id, name }) => ({
      label: name,
      value: id,
    }),
  );
  organizationOptions.unshift({
    label: 'Select organization',
    value: undefined,
  });

  const typeOptions = [
    { label: t('types.title'), value: undefined },
    { label: t('transfer'), value: 'Transfer' },
    { label: t('types.activity'), value: 'Activity' },
    { label: t('types.extra'), value: 'Extra' },
    { label: t('types.program'), value: 'Program' },
    {
      label: t('types.transitionProgram'),
      value: 'Program.transition',
    },
    { label: t('types.programFit'), value: 'Program.fit' },
    { label: t('types.programSic'), value: 'Program.sic' },
    { label: t('types.programSeries'), value: 'Program.series' },
  ];

  const extractExperiencesCompatibilities = async () => {
    setExtractProcessing(true);
    try {
      const response = await exportExperiencesCompatibilities({
        filters: { organizationId: selectedOrganization?.value },
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const href = window.URL.createObjectURL(blob);

      const anchorElement = document.createElement('a');
      anchorElement.href = href;
      anchorElement.download = 'experiences-compatibitilies.xlsx';
      document.body.appendChild(anchorElement);
      anchorElement.click();
      document.body.removeChild(anchorElement);
      window.URL.revokeObjectURL(href);
    } catch (error) {
      sendErrorSnack(error);
    } finally {
      setExtractProcessing(false);
    }
  };

  const { data: themesData, isLoading: themesLoading } = useAsync({
    promiseFn: getThemes,
  });
  const { data: segmentsData, isLoading: segmentsLoading } = useAsync({
    promiseFn: getSegments,
  });
  const themes = themesData?.data ?? [];
  const segments = segmentsData?.data ?? [];
  const languageOptions = getLanguages();
  const sustainableLevels = [
    { label: t('form.zero'), value: 1 },
    { label: t('form.low'), value: 2 },
    { label: t('form.medium'), value: 3 },
    { label: t('form.high'), value: 4 },
  ];
  const shouldShowToolTip =
    !selectedOrganization?.value && user.role === RoleEnum.KOOB_ADMIN;
  const canImportMatrix = [
    RoleEnum.KOOB_ADMIN,
    RoleEnum.DMC_ADMIN,
    RoleEnum.DMC_USER,
  ].includes(user.role);

  const updateFilterValue = useCallback(
    (property, value) => {
      setFilters((current) => ({
        ...current,
        [property]: value,
      }));
    },
    [setFilters],
  );

  return (
    <>
      <Header />

      <Container variant="main" size="full">
        <KoobListPageLayout
          width="full"
          subtitle={t('misc:resultsCount', {
            count: Number(totalCount) || 0,
          })}
          actions={
            <>
              <KoobTooltip
                tip={shouldShowToolTip ? t('extractCompatibilitiesError') : ''}
                position="top"
                hasArrow={true}
                disabled={!shouldShowToolTip}
              >
                <div className="inline-block">
                  <KoobButtonMenu
                    label={t('compatibilityMenu.label')}
                    icon="fa-regular fa-arrow-right-arrow-left"
                    loading={extractProcessing}
                    disabled={shouldShowToolTip}
                    menu={
                      <>
                        <KoobMenuItem
                          icon="fa-regular fa-download"
                          onClick={extractExperiencesCompatibilities}
                        >
                          {t('compatibilityMenu.export')}
                        </KoobMenuItem>
                        {canImportMatrix && (
                          <KoobMenuItem
                            icon="fa-regular fa-upload"
                            onClick={() => setImportOpen(true)}
                          >
                            {t('compatibilityMenu.import')}
                          </KoobMenuItem>
                        )}
                      </>
                    }
                  />
                </div>
              </KoobTooltip>
              <KoobCreateButton to="create">{t('create')}</KoobCreateButton>
              {canImportMatrix && (
                <CompatibilityImportModal
                  key={
                    selectedOrganization?.value ||
                    user.currentOrganization?.id ||
                    'current'
                  }
                  open={importOpen}
                  onClose={() => setImportOpen(false)}
                  organizationId={selectedOrganization?.value}
                  onImported={reload}
                />
              )}
            </>
          }
          search={
            <LocationAwareSearch
              name="search"
              placeholder={t('misc:searchByNameOrId')}
            />
          }
          filters={
            <>
              <KoobInputSegmented
                options={stateOptions}
                value={state ?? null}
                onChange={(value) =>
                  setSearchParams({
                    ...searchParams,
                    state: value,
                  })
                }
              />

              <div className="w-36 shrink-0">
                <KoobInputSelect
                  value={type?.value}
                  onValueChange={(value) =>
                    setType(
                      typeOptions.find((option) => option.value === value),
                    )
                  }
                  placeholder={t('types.title')}
                  options={typeOptions}
                />
              </div>

              {user.role === 'koob_admin' && (
                <div className="w-44 shrink-0">
                  <KoobInputSelect
                    value={selectedOrganization?.value}
                    onValueChange={(value) =>
                      setSelectedOrganization(
                        organizationOptions.find(
                          (option) => option.value === value,
                        ),
                      )
                    }
                    loading={organizationsLoading}
                    placeholder="Select organization"
                    options={organizationOptions}
                  />
                </div>
              )}

              <FilterExperiences
                filters={filters}
                updateFilterValue={updateFilterValue}
                onReset={() => setFilters({})}
                languageOptions={languageOptions}
                themes={themes}
                themesLoading={themesLoading}
                segments={segments}
                segmentsLoading={segmentsLoading}
                sustainableLevels={sustainableLevels}
              />
            </>
          }
          toolbarActions={
            <>
              <KoobSegmentedControl value={kind} onValueChange={setKind}>
                <KoobSegmentedControlItem value="list">
                  <i className="fa-light fa-list" style={{ fontSize: 20 }} />
                </KoobSegmentedControlItem>
                <KoobSegmentedControlItem value="map">
                  <i className="fa-light fa-map" style={{ fontSize: 20 }} />
                </KoobSegmentedControlItem>
              </KoobSegmentedControl>
            </>
          }
        >
          {kind === viewKind.LIST && (
            <>
              <ExperiencesListComponent
                experiences={experiences}
                isLoading={isLoading}
                isEmpty={isEmpty}
                setPage={setPage}
                setTotalPages={setTotalPages}
                reload={reload}
              />

              {!isEmpty && (
                <KoobPagination
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  perPage={perPage}
                  setPerPage={setPerPage}
                />
              )}
            </>
          )}
          {kind === viewKind.MAP && (
            <ExperiencesMap
              experiences={experiences}
              isLoading={isLoading}
              isEmpty={isEmpty}
              page={page}
              setPage={setPage}
              perPage={perPage}
              setPerPage={setPerPage}
              totalPages={totalPages}
              totalCount={totalCount}
            />
          )}
        </KoobListPageLayout>
      </Container>
    </>
  );
}
